import * as React from "react";
import { Pill } from "@ardept/design-system";
import type { UnverifiedSenderDto } from "../api/types.js";

/**
 * Card #3362 — the job detail's unverified-sender badge, beside the status badge.
 *
 * An inbound email that reached the job from an address it neither names as a party nor has
 * written to is never dropped: it lands on the job and the engine raises an
 * `inbound_sender_unverified` marker, which the detail route projects as `unverified_senders`.
 * This badge says so, naming each sender for the operator (operator-visible only — the console is
 * never a guest surface). Renders NOTHING when the key is absent or empty, so every other job's
 * header is unchanged.
 *
 * A pure component (no query, no router) so it renders under `renderToStaticMarkup` in the node
 * test suite, the `OutstandingItemsPanel` precedent.
 */
export function unverifiedSenderText(senders: readonly UnverifiedSenderDto[]): string {
  return `Unverified sender: ${senders.map((s) => s.sender_email).join(", ")}`;
}

export function UnverifiedSenderBadge({
  senders,
}: {
  senders: readonly UnverifiedSenderDto[] | undefined;
}): JSX.Element | null {
  if (senders === undefined || senders.length === 0) return null;
  return (
    <Pill
      tone="bad"
      title="An email reached this job from an address that is not a party on it and that it has not written to. The message was kept on the job."
    >
      {unverifiedSenderText(senders)}
    </Pill>
  );
}
